<template>
  <div class="document-info rounded" :style="{ '--bg-color': bgColor }">
    <div class="row document-info__description">
      <div class="col-12" :class="showFilename ? 'col-md-5 pr-4' : 'col-md-11'">
        <span class="title">
          {{ documentTitle }}
        </span>
        <p class="content">
          {{ uploadedFile.documentTypeName }}
        </p>
      </div>
      <div
        v-if="showFilename"
        class="col-12 col-md-3 pl-md-4 d-md-flex justify-content-md-start"
      >
        <div v-if="uploadedFile.documentName">
          <span class="title">Nombre de Archivo:</span>
          <p class="content">
            <i class="fas fa-file file-icon" />
            {{ uploadedFile.documentName }}
          </p>
        </div>
      </div>
      <div
        class="col-12 row document-info__description__actions d-flex justify-content-end"
        :class="showFilename ? 'col-md-4' : 'col-md-1'"
      >
        <action-button
          v-if="showActionBtn || filePreview"
          class="mr-2"
          i="fas fa-file-download"
          variant="success"
          @click="getFile()"
        />

        <action-button
          v-if="canReplaceDenied || canReplaceRequested"
          i="fas fa-file-plus"
          variant="outline-success"
          @click="openReplaceFileDialog"
        />
        <input
          ref="replaceFileRef"
          accept=".pdf"
          style="display: none;"
          type="file"
          @change="replaceFile"
        />

        <action-button
          v-if="canRequestReplacementApproved"
          class="custom-yelow-variant"
          i="fas fa-arrow-from-right"
          @click="$emit('request-replacement')"
        />

        <action-button
          v-if="showActionBtn"
          class="mr-2"
          i="fas fa-check"
          variant="outline-success"
          @click="$emit('manageDocumentStatus', true)"
        />

        <action-button
          v-if="showActionBtn"
          i="fas fa-ban"
          variant="outline-danger"
          @click="$emit('manageDocumentStatus', false)"
        />

        <validation-provider
          v-if="addFile"
          v-slot="{ errors }"
          name="fileUpload"
          rules="required"
        >
          <custom-file-input
            v-if="addFile"
            ref="fileUpload"
            v-model="file"
            accept=".pdf"
            :attachment-id="uploadedFile.documentId"
            :class="
              customAddFile ? 'custom-upload-document' : 'upload-document'
            "
            custom-error-msg="El documento de enmienda es requerido."
            display-style="normal"
            :error="errors[0]"
            :name="uploadedFile.documentName"
            :on-download="getFile"
            required
          />
        </validation-provider>
      </div>
    </div>
    <document-preview
      v-if="show"
      :document-name="uploadedFile.documentName"
      :document-src="documentSrc"
      :show="show"
      @close="closeModal"
    />
  </div>
</template>

<script>
import ActionButton from 'ActionButton';
import DocumentPreview from '@/views/provider/proposalHistory/components/DocumentPreview.vue';
import _generalApi from '@/modules/app/api.js';
import CustomFileInput from 'CustomFileInput';

export default {
  name: 'DocumentInfo',
  components: {
    ActionButton,
    DocumentPreview,
    CustomFileInput,
  },
  props: {
    document: {
      type: Object,
      required: true,
      default: () => {},
    },
    addFile: {
      type: Boolean,
      default: () => false,
    },
    customAddFile: {
      type: Boolean,
      default: () => true,
    },
    filePreview: {
      type: Boolean,
      default: () => true,
    },
    canReplaceDenied: {
      type: Boolean,
      default: () => false,
    },
    canRequestReplacementApproved: {
      type: Boolean,
      default: () => false,
    },
    canReplaceRequested: {
      type: Boolean,
      default: () => false,
    },
    showActionBtn: {
      type: Boolean,
      default: () => false,
    },
    showFilename: {
      type: Boolean,
      default: () => false,
    },
    documentTitle: {
      type: String,
      default: () => 'Tipo:',
    },
    bgColor: {
      type: String,
      default: () => 'secondary-3',
    },
  },
  data: () => ({
    show: false,
    documentSrc: null,
    file: null,
    uploadedFile: {},
  }),
  computed: {
    canDownload() {
      return this.userHasPermissions('proposal.candownloaddocument');
    },
  },
  watch: {
    file: {
      immediate: true,
      handler(current) {
        if (current) {
          const uploadFile = current;
          this.$emit('on-upload-file', uploadFile);
          this.uploadedFile.documentName = uploadFile.name;
        } else if (!this.customAddFile) {
          this.uploadedFile.documentName = null;
        }
      },
    },
    document: {
      immediate: true,
      handler(current) {
        if (current) {
          this.uploadedFile = this.document;
        }
      },
    },
  },
  methods: {
    openReplaceFileDialog() {
      this.$refs['replaceFileRef'].click();
    },
    replaceFile(event) {
      if (!event?.target?.files[0]) return;

      this.file = event.target.files[0];
    },
    async getFile() {
      if (this.canDownload) {
        await this.downloadDocument();
      } else {
        await this.previewDocument();
      }
    },
    async downloadDocument() {
      await _generalApi.downloadDocumentByAttachmentId(
        this.document.documentId
      );
    },
    async previewDocument() {
      await _generalApi
        .getDocumentByAttachmentId(this.document.documentId)
        .then((response) => {
          if (response) {
            const linkData = this.$router.resolve({
              name: 'DocumentViewer',
              query: {
                documentId: this.document.documentId,
                docName: this.uploadedFile.documentName,
              },
            });

            window.open(linkData.href, '_blank');
          }
        });
    },
    closeModal() {
      this.show = false;
    },
  },
};
</script>
<style scoped lang="scss">
@import '~@/shared/scss/config/colors.scss';

.custom-yelow-variant {
  background-color: #f4b940;
  color: white;
}

.document-info {
  --bg-color: map-get($colors, 'secondary-3');
  background-color: var(--bg-color);
  padding: 10px 30px;

  &__description {
    display: flex;
    align-items: center;

    .content {
      margin-top: 10px;
    }
    .title {
      color: map-get($colors, 'secondary-6');
      font-size: 0.875rem;
    }
    .file-icon {
      color: map-get($colors, 'orange');
      margin-right: 0.9rem !important;
    }
    @media only screen and (max-width: 768px) {
      .title,
      .content {
        font-size: 0.8572rem;
      }
    }

    ::v-deep &__actions {
      margin-left: 0px;
      .show-icon i {
        margin-right: 0px !important;
      }

      .font-weight-normal {
        label {
          display: none;
        }
      }

      .upload-document {
        .file-select {
          cursor: pointer;
          span {
            font-size: 1rem;
            @media screen and (max-width: 768px) {
              font-size: 0.7857rem;
            }
          }
          em {
            @media screen and (max-width: 768px) {
              padding: 0.4286rem !important;
            }
          }
        }
      }

      .custom-upload-document {
        .file-input-container {
          background-color: map-get($colors, 'green');
          border: 1px solid map-get($colors, 'green');

          .file-select {
            cursor: pointer;
            span {
              display: none;
            }
            em {
              padding: 0px !important;
              color: map-get($colors, 'white');
            }
          }
        }
      }
    }
  }
}
</style>
